import { Button, Checkbox, Drawer, Ellipsis } from '@seiue/ui'
import { cloneDeep, filter, map, sortBy } from '@seiue/util'
import React, { useEffect, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd'
import styled from 'styled-components'

import { GrowpIcon } from 'packages/components/GrowpIcon'

import { getTheme } from '@/utils/theme'

import { HeaderData, HeaderSortingProps } from './types'

/**
 * 表头排序组件
 *
 * @param param - 参数
 * @param param.visible - 是否显示抽屉
 * @param param.headerData - 表头数据
 * @param param.onClose - 关闭回调
 * @param param.onOk - 确定回调
 * @param param.visibleControl - 是否启用表头显示与隐藏设置【默认关闭】
 * @param param.hasLeft - 是否已离校
 * @returns React.ReactNode
 */
export const HeaderSorting: React.FC<HeaderSortingProps> = ({
  headerData,
  visible,
  onClose,
  onOk,
  visibleControl = false,
  hasLeft,
}) => {
  const theme = getTheme()
  const sortData = sortBy(headerData, hasLeft ? 'archivedWeight' : 'weight')
  const [dragItems, setDragItems] = useState<HeaderData[]>(sortData || [])

  useEffect(() => {
    if (visible) {
      setDragItems([...sortData])
    }
  }, [visible])

  const onDragEnd = (result: DropResult) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return
    }
    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index
    const cloneItems = cloneDeep(dragItems)
    const [removed] = cloneItems.splice(sourceIndex, 1)
    cloneItems.splice(destinationIndex, 0, removed)
    const resultItems = cloneItems.map((item, index) => ({
      ...item,
      [hasLeft ? 'archivedWeight' : 'weight']: index,
    }))
    setDragItems(resultItems)
  }

  const checkedItemIds = map(
    filter(dragItems, { [hasLeft ? 'archivedIsShow' : 'isShow']: true }),
    'id'
  )

  const checkedAll = dragItems.every((item) =>
    hasLeft ? item.archivedIsShow : item.isShow
  )
  const indeterminate = dragItems.some((item) =>
    hasLeft ? item.archivedIsShow : item.isShow
  )
  return (
    <Drawer
      title="表头管理"
      width={320}
      visible={visible}
      onClose={onClose}
      headerStyle={{
        minHeight: '64px',
      }}
      bodyStyle={{
        padding: '16px 8px',
      }}
      footer={[
        <BottomBtns key="btns">
          <Button onClick={() => onClose()}>取消</Button>
          <Button
            type="primary"
            onClick={() => onOk(dragItems)}
            style={{
              marginRight: 0,
            }}
          >
            保存
          </Button>
        </BottomBtns>,
      ]}
    >
      <TopInfo>请选择列表中要展示的信息，上下拖拽调整位置</TopInfo>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="list">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {visibleControl ? (
                <CheckAllWrapper>
                  <Checkbox
                    indeterminate={!checkedAll && indeterminate}
                    checked={checkedAll}
                    onChange={(e) => {
                      const checked = e.target.checked
                      const updateDragItems = map(dragItems, (item) => ({
                        ...item,
                        [hasLeft ? 'archivedIsShow' : 'isShow']: item.disabled
                          ? item.isShow
                          : checked,
                      }))
                      setDragItems(updateDragItems)
                    }}
                  >
                    全部
                  </Checkbox>
                </CheckAllWrapper>
              ) : null}
              <Checkbox.Group
                value={checkedItemIds}
                onChange={(v) => {
                  const updateDragItems = map(dragItems, (item) => ({
                    ...item,
                    [hasLeft ? 'archivedIsShow' : 'isShow']: v.includes(
                      item.id
                    ),
                  }))
                  setDragItems(updateDragItems)
                }}
                style={{
                  width: '100%',
                }}
              >
                {dragItems.map((hd, i) => {
                  return (
                    <React.Fragment key={hd.id}>
                      <Draggable
                        draggableId={`${hd.id}`}
                        index={i}
                      >
                        {(provided) => (
                          <DragItemWrapper
                            key={hd.id}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            {visibleControl ? (
                              <Checkbox
                                value={hd.id}
                                disabled={hd.disabled}
                              >
                                <div
                                  style={{
                                    width: '242px',
                                  }}
                                >
                                  <Ellipsis text={hd.label} />
                                </div>
                              </Checkbox>
                            ) : (
                              <div
                                style={{
                                  width: '242px',
                                }}
                              >
                                <Ellipsis text={hd.label} />
                              </div>
                            )}
                            <GrowpIcon
                              name="DragVertical"
                              color={theme.text._3}
                              style={{
                                cursor: 'move',
                              }}
                            />
                          </DragItemWrapper>
                        )}
                      </Draggable>
                    </React.Fragment>
                  )
                })}
              </Checkbox.Group>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Drawer>
  )
}

const BottomBtns = styled.div`
  display: flex;
  justify-content: flex-end;
`

const TopInfo = styled.div`
  width: 100%;
  height: 20px;
  font-size: 12px;
  line-height: 20px;
  margin-bottom: 8px;
  padding-left: 8px;
  color: ${(props) => props.theme.text._3};
`

const CheckAllWrapper = styled.div`
  width: 100%;
  height: 40px;
  padding-left: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DragItemWrapper = styled(CheckAllWrapper)`
  padding: 0 8px;
  border-radius: 8px;
  cursor: move;
  &:hover {
    background-color: ${(props) => props.theme.background._3};
  }
`
